import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { SettingsTour } from "@/components/ProductTour";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { SettingsSectionBack, SettingsSectionHub } from "@/components/settings/SettingsSectionHub";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Factory, Palette } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { useSession } from "@/hooks/use-session";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  normalizeSettingsTab,
  settingsHubSections,
  settingsSectionById,
  settingsSectionsForRole,
  type SettingsTabId,
} from "@/lib/settings-sections";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { PublishSettingsPanel } from "@/components/PublishSettingsPanel";
import {
  parseWidgetConfig,
  type WidgetPublishConfig,
  DEFAULT_WIDGET_CONFIG,
} from "@/lib/widget-publish";
import type { SmPlanLimits } from "@/lib/plan-limits";
import { TaxDefaultsPanel } from "@/components/settings/TaxDefaultsPanel";
import { PriceRoundingPanel } from "@/components/settings/PriceRoundingPanel";
import {
  BusinessProfilePanel,
  PaymentMethodsPanel,
  EMPTY_NORMALIZED_ADDRESS,
  type NormalizedAddress,
} from "@/components/settings/MarketSettingsPanels";
import {
  formatNormalizedAddressLine,
  normalizeArPostalCode,
  normalizedAddressFromDb,
  validateNormalizedAddress,
} from "@structura/core/address";
import type { BranchTypeKey } from "@/lib/business-profile";
import { branchTypesForBusinessProfile } from "@/lib/business-profile";
import { CommunicationCenter } from "@/components/settings/CommunicationCenter";
import {
  SeoPanel,
  SubscriptionSettingsPanel,
  PoliciesPanel,
  TrackingPanel,
  parseTrackingForm,
  trackingFormToPatch,
  type TrackingFormState,
} from "@/components/settings/MarketSettingsPanels";
import {
  LazyTeamSettingsPanel,
  LazyUsageGaugesPanel,
  LazyApiKeysPanel,
  LazyTiendaSettingsPanel,
  LazyEntregaSettingsPanel,
  SettingsPanelSuspense,
} from "@/components/settings/lazy-settings-panels";
import { TenantSupportPanel } from "@/components/settings/TenantSupportPanel";
import { IntegracionesHub } from "@/components/settings/IntegracionesHub";
import { ServicesCalendarIntegracionesPanel } from "@/components/settings/ServicesCalendarIntegracionesPanel";
import { ProviderHub } from "@/components/ds/ProviderHub";
import { CustomDomainField } from "@/components/CustomDomainField";
import { CONFIG_PRINTERS_LINK, CONFIG_LOGISTICS_LINK, CONFIG_SUCURSALES_LINK, CONFIG_EQUIPO_LINK } from "@/lib/workspace-hubs";

export const Route = createFileRoute("/app/settings")({
  component: SettingsRoot,
  // Las tres claves son opcionales: `calendar`/`calendar_error` solo llegan en el
  // retorno del OAuth de agenda, y quien navega a un tab no tiene por qué pasarlas.
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    tab?: SettingsTabId;
    calendar?: string;
    calendar_error?: string;
  } => ({
    tab: normalizeSettingsTab(typeof s.tab === "string" ? s.tab : undefined) ?? undefined,
    calendar: typeof s.calendar === "string" ? s.calendar : undefined,
    calendar_error: typeof s.calendar_error === "string" ? s.calendar_error : undefined,
  }),
  head: () => ({ meta: [{ title: "Configuración — Structura Market" }] }),
});

/**
 * Wrapper that delegates to child routes (printers, billing) when active.
 * Separate component so no hooks are violated — useChildMatches is the
 * only hook here, and the early return is safe.
 */
function SettingsRoot() {
  const { pathname } = useLocation();
  // Delegate to child routes like /printers, /billing
  const isChildRoute = pathname !== "/app/settings" && pathname.startsWith("/app/settings/");
  if (isChildRoute) return <Outlet />;
  return <SettingsPage />;
}

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string | null;
  address?: string | null;
  description?: string | null;
  logoUrl?: string | null;
  socialLinks?: Record<string, string>;
  trialEndsAt?: string | null;
  lifecycleStatus?: string | null;
  customDomain?: string | null;
  customDomainStatus?: string | null;
  customDomainVerifiedAt?: string | null;
  customDomainCheckError?: string | null;
}

interface ConfigForm {
  primaryColor: string;
  secondaryColor: string;
  heroTitle: string;
  heroSubtitle: string;
  heroImageUrl: string;
  heroVideoUrl: string;
  metodoEntrega: string;
  radioDeliveryKm: string;
  deliveryZonesEnabled: boolean;
  deliveryDistanceTiers: Array<{ minKm: number; maxKm: number | null; priceCents: number | null }>;
  whatsappNumber: string;
  whatsappMessageTemplate: string;
  mpAccessToken: string;
  mpPublicKey: string;
  carruselesActivos: { tag: string; label: string; order: number }[];
  businessProfile: string;
  enabledFeatures: Record<string, boolean>;
}

const DEFAULT_CARRUSELES = [
  { tag: "Oferta", label: "Ofertas", order: 0 },
  { tag: "Lo más pedido", label: "Lo más pedido", order: 1 },
  { tag: "Fresco", label: "Fresco", order: 2 },
];

function SettingsPage() {
  const tid = useWorkspaceTenantId();
  const { productFlags } = useTenantContext();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useSession();
  const { tab: tabSearch } = Route.useSearch();
  const activeTab = normalizeSettingsTab(tabSearch);
  const showHub = !activeTab;
  const activeSection = activeTab ? settingsSectionById(activeTab) : null;

  // Entrega/Envíos se movieron a /app/logistica
  useEffect(() => {
    if (activeTab === "entrega") {
      void navigate({ to: "/app/logistica", search: { provider: "entrega" } });
    } else if (activeTab === "envios") {
      void navigate({ to: "/app/logistica", search: { provider: "shipnow" } });
    } else if (activeTab === "salon") {
      void navigate({ to: "/app/salon", search: { config: true } });
    } else if (activeTab === "equipo") {
      void navigate({ to: "/app/equipo" });
    } else if (activeTab === "b2b") {
      void navigate({ to: "/app/produccion/reglas", search: {} as never });
    }
  }, [activeTab, navigate]);

  const goToTab = (tab: SettingsTabId) => {
    // Some sections have dedicated routes
    if (tab === "impresoras") {
      void navigate({ to: "/app/settings/printers", search: {} as never });
      return;
    }
    if (tab === "config-financiera") {
      void navigate({ to: "/app/settings/config-financiera", search: {} as never });
      return;
    }
    if (tab === "entrega") {
      void navigate({ to: "/app/logistica", search: { provider: "entrega" } });
      return;
    }
    if (tab === "salon") {
      void navigate({ to: "/app/salon", search: { config: true } });
      return;
    }
    if (tab === "equipo") {
      void navigate({ to: "/app/equipo" });
      return;
    }
    if (tab === "b2b") {
      void navigate({ to: "/app/produccion/reglas", search: {} as never });
      return;
    }
    if (tab === "envios") {
      void navigate({ to: "/app/logistica", search: { provider: "shipnow" } });
      return;
    }
    void navigate({ to: "/app/settings", search: { tab } });
  };
  const goToHub = () => {
    void navigate({ to: "/app/settings", search: {} as never });
  };

  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [config, setConfig] = useState<ConfigForm | null>(null);
  const setConfigSnippet = (c: Record<string, unknown>) => setConfig((prev) => prev ? { ...prev, ...c } : (c as unknown as ConfigForm));
  const visibleSections = settingsSectionsForRole(user?.role, config?.businessProfile, { productFlags });
  const hubSections = settingsHubSections(user?.role, config?.businessProfile, { productFlags });
  const canAccessActiveTab =
    !activeTab || visibleSections.some((s) => s.id === activeTab);
  const [tenantName, setTenantName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [planId, setPlanId] = useState<string | null>(null);
  const [planLimits, setPlanLimits] = useState<SmPlanLimits | null>(null);
  const [planValidUntil, setPlanValidUntil] = useState<string | null>(null);
  const [widgetConfig, setWidgetConfig] = useState<WidgetPublishConfig>(DEFAULT_WIDGET_CONFIG);
  const [trackingBase, setTrackingBase] = useState<Record<string, unknown>>({});
  const [trackingForm, setTrackingForm] = useState<TrackingFormState>(parseTrackingForm({}));
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [geoAddress, setGeoAddress] = useState<NormalizedAddress>({ ...EMPTY_NORMALIZED_ADDRESS });
  const [branchType, setBranchType] = useState<BranchTypeKey>("store");
  const [showAddressOnStorefront, setShowAddressOnStorefront] = useState(true);
  const [description, setDescription] = useState("");
  const [socialLinks, setSocialLinks] = useState<Record<string, string>>({});
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [seoOgImage, setSeoOgImage] = useState("");
  const [deadlineHour, setDeadlineHour] = useState("10");
  const [cutoffHours, setCutoffHours] = useState("24");
  const [storefrontTimezone, setStorefrontTimezone] = useState("America/Argentina/Buenos_Aires");
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [bankHolder, setBankHolder] = useState("");
  const [bankCbu, setBankCbu] = useState("");
  const [bankAlias, setBankAlias] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankNotes, setBankNotes] = useState("");
  const [waProvider, setWaProvider] = useState("");
  const [waOrderConfirmEnabled, setWaOrderConfirmEnabled] = useState(false);
  const [waCredentials, setWaCredentials] = useState<Record<string, string>>({});
  const [cancelHours, setCancelHours] = useState("2");
  const [prepayDomicilio, setPrepayDomicilio] = useState(true);
  const [allowNegativeStock, setAllowNegativeStock] = useState(false);
  const [posInvoicingRequired, setPosInvoicingRequired] = useState(false);
  const [allowNegativeStockLocationIds, setAllowNegativeStockLocationIds] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const [tRes, cRes, subRes, usageRes] = await Promise.all([
        apiGetJson<{ tenant: TenantInfo; planId?: string | null; planLimits?: SmPlanLimits }>("/api/tenant", tid),
        apiGetJson<{ config: Record<string, unknown> | null }>("/api/tenant/config", tid),
        apiGetJson<{ subscription?: { plan_id?: string } | null }>("/api/subscriptions", tid).catch(() => ({
          subscription: null,
        })),
        apiGetJson<{ planLimits?: SmPlanLimits }>("/api/plan-usage", tid).catch(() => ({ planLimits: null })),
      ]);
      setPlanId(subRes.subscription?.plan_id ?? tRes.planId ?? null);
      setPlanLimits(usageRes.planLimits ?? tRes.planLimits ?? null);
      setPlanValidUntil((subRes.subscription as Record<string, unknown> | null)?.valid_until as string | null ?? null);
      setTenant(tRes.tenant);
      setTenantName(tRes.tenant.name);
      setLogoUrl(String(tRes.tenant.logoUrl ?? ""));
      setPhone(String(tRes.tenant.phone ?? ""));
      setAddress(String(tRes.tenant.address ?? ""));
      setDescription(String(tRes.tenant.description ?? ""));
      setSocialLinks((tRes.tenant.socialLinks as Record<string, string>) ?? {});
      setShowAddressOnStorefront(cRes.config?.showAddressOnStorefront !== false);
      const locs = await apiGetJson<Array<{
        address?: string | null;
        latitude?: number | null;
        longitude?: number | null;
        placeId?: string | null;
        branchType?: string | null;
        isDefault?: boolean;
        street?: string | null;
        streetNumber?: string | null;
        city?: string | null;
        province?: string | null;
        postalCode?: string | null;
        country?: string | null;
      }>>("/api/locations", tid).catch(() => []);
      const primary = locs.find((l) => l.isDefault) ?? locs[0] ?? null;
      if (primary) {
        setGeoAddress(
          normalizedAddressFromDb({
            street: primary.street,
            street_number: primary.streetNumber,
            city: primary.city,
            province: primary.province,
            postal_code: primary.postalCode,
            country: primary.country ?? "AR",
            address: primary.address || String(tRes.tenant.address ?? ""),
            latitude: primary.latitude,
            longitude: primary.longitude,
            place_id: primary.placeId,
          }),
        );
        const allowed = branchTypesForBusinessProfile(String(cRes.config?.businessProfile ?? "catalog"));
        const bt = (primary.branchType as BranchTypeKey | undefined) ?? "store";
        setBranchType(allowed.includes(bt) ? bt : allowed[0] ?? "store");
      } else if (tRes.tenant.address) {
        setGeoAddress({
          ...EMPTY_NORMALIZED_ADDRESS,
          formattedAddress: String(tRes.tenant.address),
        });
      } else {
        setGeoAddress({ ...EMPTY_NORMALIZED_ADDRESS });
      }
      const c = cRes.config;
      setConfig({
        primaryColor: String(c?.primaryColor ?? "#16a34a"),
        secondaryColor: String(c?.secondaryColor ?? "#15803d"),
        heroTitle: String(c?.heroTitle ?? tRes.tenant.name),
        heroSubtitle: String(c?.heroSubtitle ?? ""),
        heroImageUrl: String(c?.heroImageUrl ?? ""),
        heroVideoUrl: String(c?.heroVideoUrl ?? ""),
        metodoEntrega: String(c?.metodoEntrega ?? "ambos"),
        radioDeliveryKm: c?.radioDeliveryKm != null ? String(c.radioDeliveryKm) : "",
        deliveryZonesEnabled: Boolean(c?.deliveryZonesEnabled),
        deliveryDistanceTiers: Array.isArray(c?.deliveryDistanceTiers) ? c.deliveryDistanceTiers : [],
        whatsappNumber: String(c?.whatsappNumber ?? ""),
        whatsappMessageTemplate: String(c?.whatsappMessageTemplate ?? ""),
        mpAccessToken: String(c?.mpAccessToken ?? ""),
        mpPublicKey: String(c?.mpPublicKey ?? ""),
        carruselesActivos:
          (c?.carruselesActivos as ConfigForm["carruselesActivos"])?.length
            ? (c?.carruselesActivos as ConfigForm["carruselesActivos"])
            : DEFAULT_CARRUSELES,
        businessProfile: String(c?.businessProfile ?? "catalog"),
        enabledFeatures: (c?.enabledFeatures as Record<string, boolean>) ?? {},
      });
      setBackgroundColor(String(c?.backgroundColor ?? "#ffffff"));
      const tr = (c?.tracking as Record<string, unknown>) ?? {};
      setTrackingBase(tr);
      setTrackingForm(parseTrackingForm(tr));
      setWidgetConfig(parseWidgetConfig(tr));
      setWaProvider(String(c?.waProvider ?? ""));
      setWaOrderConfirmEnabled(Boolean(c?.waOrderConfirmEnabled));
      setWaCredentials((c?.waCredentials as Record<string, string>) ?? {});
      const policies = (c?.storePolicies as Record<string, unknown>) ?? {};
      setCancelHours(String(policies.cancelHours ?? 2));
      setPrepayDomicilio(policies.prepayDomicilio !== false);
      setAllowNegativeStock(policies.allowNegativeStock === true);
      setPosInvoicingRequired(policies.posInvoicingPolicy === "required");
      setAllowNegativeStockLocationIds(
        Array.isArray(policies.allowNegativeStockLocationIds)
          ? (policies.allowNegativeStockLocationIds as string[])
          : [],
      );
      setSeoTitle(String(c?.seoMetaTitle ?? ""));
      setSeoDescription(String(c?.seoMetaDescription ?? ""));
      setSeoOgImage(String(c?.seoOgImageUrl ?? ""));
      setDeadlineHour(String(c?.institutionalOrderDeadlineHour ?? 10));
      setCutoffHours(String(c?.institutionalCutoffHours ?? 24));
      setStorefrontTimezone(String(c?.storefrontTimezone ?? "America/Argentina/Buenos_Aires"));
      setPaymentMethods(Array.isArray(c?.paymentMethods) ? (c.paymentMethods as string[]) : []);
      setBankHolder(String(c?.bankTransferHolder ?? ""));
      setBankCbu(String(c?.bankTransferCbu ?? ""));
      setBankAlias(String(c?.bankTransferAlias ?? ""));
      setBankName(String(c?.bankTransferBank ?? ""));
      setBankNotes(String(c?.bankTransferNotes ?? ""));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveTenant = async () => {
    if (!tid) return;
    setBusy(true);
    try {
      await apiSendJson("/api/tenant", "PATCH", { name: tenantName, logo_url: logoUrl || null }, tid);
      toast.success("Negocio guardado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const saveConfig = async (patch: Record<string, unknown>) => {
    if (!tid) return;
    setBusy(true);
    try {
      await apiSendJson("/api/tenant/config", "PATCH", patch, tid);
      toast.success("Configuración guardada");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  if (!tid) {
    return (
      <div>
        <DashboardHeader title="Configuración" />
        <p className="p-6 text-sm text-muted-foreground">Seleccioná un workspace para configurar la tienda.</p>
      </div>
    );
  }

  if (loading || !config || !tenant) {
    return (
      <div>
        <DashboardHeader title="Configuración" />
        <PageLoader label="Cargando configuración…" />
      </div>
    );
  }

  const hasStorefront = planLimits?.storefront ?? false;

  const saveWidgetConfig = async () => {
    if (!tid) return;
    setBusy(true);
    try {
      await apiSendJson(
        "/api/tenant/config",
        "PATCH",
        {
          tracking: {
            ...trackingBase,
            ...trackingFormToPatch(trackingForm),
            widget: widgetConfig,
          },
        },
        tid,
      );
      toast.success("Widget guardado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const saveTrackingAnalytics = async () => {
    if (!tid) return;
    setBusy(true);
    try {
      await apiSendJson(
        "/api/tenant/config",
        "PATCH",
        {
          tracking: {
            ...trackingBase,
            ...trackingFormToPatch(trackingForm),
          },
        },
        tid,
      );
      toast.success("Tracking guardado");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const saveBusinessProfile = async () => {
    if (!tid) return;
    const addrErr = validateNormalizedAddress(geoAddress, { requireGeocoded: true });
    if (addrErr) {
      toast.error(addrErr);
      return;
    }
    const lat = Number(geoAddress.latitude);
    const lng = Number(geoAddress.longitude);
    const addressLine =
      formatNormalizedAddressLine({
        ...geoAddress,
        postalCode: normalizeArPostalCode(geoAddress.postalCode),
      }) || geoAddress.formattedAddress.trim();
    if (!addressLine) {
      toast.error("La dirección es obligatoria");
      return;
    }
    setBusy(true);
    try {
      const mapsUrl = geoAddress.placeId?.trim()
        ? `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${encodeURIComponent(geoAddress.placeId.trim())}`
        : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      await apiSendJson(
        "/api/tenant",
        "PATCH",
        {
          name: tenantName,
          phone: phone || null,
          address: addressLine,
          description: description || null,
          socialLinks,
          latitude: lat,
          longitude: lng,
          placeId: geoAddress.placeId?.trim() || null,
          mapsUrl,
          branchType,
          street: geoAddress.street.trim(),
          streetNumber: geoAddress.streetNumber.trim(),
          city: geoAddress.city.trim(),
          province: geoAddress.province.trim(),
          postalCode: normalizeArPostalCode(geoAddress.postalCode),
          country: geoAddress.country?.trim() || "AR",
        },
        tid,
      );
      await apiSendJson(
        "/api/tenant/config",
        "PATCH",
        {
          business_profile: config.businessProfile,
          show_address_on_storefront: showAddressOnStorefront,
        },
        tid,
      );
      setAddress(addressLine);
      toast.success("Perfil guardado — dirección sincronizada como sucursal principal");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const togglePaymentMethod = (id: string) => {
    setPaymentMethods((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    );
  };

  return (
    <div className={cn(isMobile && "min-h-screen bg-background pb-8")}>
      <SettingsTour />
      {!isMobile ? (
        <DashboardHeader
          title="Configuración del negocio"
          subtitle="Operación, pagos, publicación y cuenta"
          layout="sticky"
        />
      ) : (
        <div className="px-4 pt-4">
          <h1 className="text-headline-md font-bold text-foreground">Configuración</h1>
          <p className="text-body-md text-muted-foreground">Operación del negocio</p>
        </div>
      )}
      <ClinicalPageShell className={cn("mx-auto w-full max-w-[1400px] space-y-5", isMobile && "px-4 pt-4")}>
        {showHub ? (
          <SettingsSectionHub
            sections={hubSections}
            links={[
              { id: "experience", label: "Experiencia de la tienda", description: "Colores, home, header y bloques", href: "/app/experience", icon: Palette, cluster: "tienda" as const },
              { id: "b2b-hub", label: "Portal B2B", description: "Pedidos mayoristas y reglas", href: "/app/produccion", icon: Factory, cluster: "clientes" as const },
              { ...CONFIG_SUCURSALES_LINK, cluster: "negocio" as const },
              { ...CONFIG_EQUIPO_LINK, cluster: "equipo" as const },
              { ...CONFIG_LOGISTICS_LINK, cluster: "operacion" as const },
              { ...CONFIG_PRINTERS_LINK, cluster: "operacion" as const },
            ]}
            activeTab={activeTab}
            onSelect={(tab) => goToTab(tab as SettingsTabId)}
          />
        ) : activeSection && !canAccessActiveTab ? (
          <p className="text-sm text-muted-foreground">
            No tenés acceso a esta sección.{" "}
            <button
              type="button"
              className="text-primary underline-offset-2 hover:underline"
              onClick={goToHub}
            >
              Volver a configuración
            </button>
          </p>
        ) : activeSection ? (
          <>
            <SettingsSectionBack
              title={activeSection.label}
              description={activeSection.description}
              onBack={activeTab === "agenda" ? () => goToTab("integraciones") : goToHub}
              backLabel={activeTab === "agenda" ? "Integraciones" : "Configuración"}
            />

          {activeTab === "negocio" ? (
            <BusinessProfilePanel
              tenantName={tenantName}
              setTenantName={setTenantName}
              phone={phone}
              setPhone={setPhone}
              description={description}
              setDescription={setDescription}
              socialLinks={socialLinks}
              setSocialLinks={setSocialLinks}
              businessProfile={config.businessProfile}
              setBusinessProfile={(v) => {
                setConfig({ ...config, businessProfile: v });
                const allowed = branchTypesForBusinessProfile(v);
                if (!allowed.includes(branchType)) {
                  setBranchType(allowed[0] ?? "store");
                }
              }}
              geoAddress={geoAddress}
              setGeoAddress={setGeoAddress}
              branchType={branchType}
              setBranchType={setBranchType}
              showAddressOnStorefront={showAddressOnStorefront}
              setShowAddressOnStorefront={setShowAddressOnStorefront}
              busy={busy}
              onSave={() => void saveBusinessProfile()}
              onCancel={goToHub}
            />
          ) : null}

          {activeTab === "negocio" ? <TaxDefaultsPanel tenantId={tid} /> : null}

          {activeTab === "negocio" ? <PriceRoundingPanel tenantId={tid} /> : null}

          {activeTab === "tienda" ? (
            <SettingsPanelSuspense>
              <LazyTiendaSettingsPanel
                config={config}
                setConfig={setConfigSnippet as (c: unknown) => void}
                busy={busy}
                onCancel={goToHub}
                onSave={async () => {
                  await saveConfig({
                    enabled_features: config.enabledFeatures,
                  });
                }}
              />
            </SettingsPanelSuspense>
          ) : null}

          {activeTab === "entrega" ? (
            <SettingsPanelSuspense>
              <LazyEntregaSettingsPanel
                config={config}
                setConfig={setConfigSnippet as (c: unknown) => void}
                storefrontTimezone={storefrontTimezone}
                setStorefrontTimezone={setStorefrontTimezone}
                busy={busy}
                onSaveEntrega={() =>
                  void saveConfig({
                    metodo_entrega: config.metodoEntrega,
                    radio_delivery_km: config.radioDeliveryKm ? Number(config.radioDeliveryKm) : null,
                    delivery_zones_enabled: config.deliveryZonesEnabled,
                    delivery_distance_tiers: config.deliveryDistanceTiers ?? [],
                    whatsapp_number: config.whatsappNumber || null,
                    whatsapp_message_template: config.whatsappMessageTemplate || null,
                  })
                }
                onSaveTimezone={() => void saveConfig({ storefront_timezone: storefrontTimezone })}
              />
            </SettingsPanelSuspense>
          ) : null}

          {activeTab === "pagos" ? (
          <div className="w-full max-w-[1400px] space-y-6">
            <PaymentMethodsPanel
              paymentMethods={paymentMethods}
              toggleMethod={togglePaymentMethod}
              bankHolder={bankHolder}
              setBankHolder={setBankHolder}
              bankCbu={bankCbu}
              setBankCbu={setBankCbu}
              bankAlias={bankAlias}
              setBankAlias={setBankAlias}
              bankName={bankName}
              setBankName={setBankName}
              bankNotes={bankNotes}
              setBankNotes={setBankNotes}
              busy={busy}
              onCancel={goToHub}
              onSave={() =>
                void saveConfig({
                  payment_methods: paymentMethods,
                  bank_transfer_holder: bankHolder || null,
                  bank_transfer_cbu: bankCbu || null,
                  bank_transfer_alias: bankAlias || null,
                  bank_transfer_bank: bankName || null,
                  bank_transfer_notes: bankNotes || null,
                })
              }
            />
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Pasarelas</h3>
              <p className="text-xs text-muted-foreground">Conectá cómo cobrás con tarjeta y billeteras.</p>
              <ProviderHub
                providers={["mercadopago", "payway", "getnet", "uala"]}
                backLabel="Pagos"
              />
            </div>
          </div>
          ) : null}

          {activeTab === "envios" ? (
            <ProviderHub
              providers={[
                "shipnow",
                "treggo",
                "andreani",
                "cabify",
                "zipnova",
                "pickit",
                "oca",
                "correo_argentino",
                "pedidosya",
                "rapiboy",
                "uber_direct",
              ]}
              intro="Configurá los proveedores de envío activos. Seleccioná uno para ver y editar su configuración."
            />
          ) : null}

          {activeTab === "politicas" ? (
            <PoliciesPanel
              cancelHours={cancelHours}
              setCancelHours={setCancelHours}
              prepayDomicilio={prepayDomicilio}
              setPrepayDomicilio={setPrepayDomicilio}
              allowNegativeStock={allowNegativeStock}
              setAllowNegativeStock={setAllowNegativeStock}
              posInvoicingRequired={posInvoicingRequired}
              setPosInvoicingRequired={setPosInvoicingRequired}
              busy={busy}
              onCancel={goToHub}
              onSave={() =>
                void saveConfig({
                  store_policies: {
                    cancelHours: Number(cancelHours) || 0,
                    prepayDomicilio,
                    allowNegativeStock,
                    ...(allowNegativeStockLocationIds.length
                      ? { allowNegativeStockLocationIds }
                      : {}),
                    posInvoicingPolicy: posInvoicingRequired ? "required" : "optional",
                  },
                })
              }
            />
          ) : null}

          {activeTab === "mensajeria" ? (
            <ProviderHub
              providers={["whatsapp", "kapso", "resend", "push_notifications"]}
              backLabel="Mensajería"
              intro="Configurá los canales de mensajería con tus clientes."
            />
          ) : null}

          {activeTab === "integraciones" ? <IntegracionesHub /> : null}

          {activeTab === "agenda" ? (
            <ServicesCalendarIntegracionesPanel calendarEnabled={productFlags.calendar_sync === true} />
          ) : null}

          {activeTab === "facturacion" ? (
            <ProviderHub
              providers={["tusfacturas", "arca_directo"]}
              backLabel="Facturación"
              intro="Elegí con qué proveedor emitir comprobantes electrónicos AFIP/ARCA. El tag Conectado solo aparece en el proveedor con credenciales/certificados listos (no por tener el módulo en el plan)."
              help={
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>
                    Factura electrónica ≠ ticket del controlador. Acá configurás ARCA o TusFacturas (webservice AFIP).
                    El botón «Emitir ticket fiscal» en el pedido usa hardware HIP o simulación y no reemplaza esta integración.
                  </p>
                  <p>
                    ¿Necesitás activar una integración?{" "}
                    <a href="mailto:hola@structura.com.ar" className="font-medium text-primary underline-offset-2 hover:underline">
                      Contactá a soporte
                    </a>
                  </p>
                </div>
              }
            />
          ) : null}

          {activeTab === "notificaciones" ? (
            tid ? <CommunicationCenter tenantId={tid} /> : null
          ) : null}

          {activeTab === "tracking" ? (
            <TrackingPanel
              tenantId={tid}
              productFlags={productFlags}
              form={trackingForm}
              setForm={setTrackingForm}
              busy={busy}
              onCancel={goToHub}
              onSave={() => void saveTrackingAnalytics()}
            />
          ) : null}

          {activeTab === "seo" ? (
            <SeoPanel
              seoTitle={seoTitle}
              setSeoTitle={setSeoTitle}
              seoDescription={seoDescription}
              setSeoDescription={setSeoDescription}
              seoOgImage={seoOgImage}
              setSeoOgImage={setSeoOgImage}
              busy={busy}
              onCancel={goToHub}
              tenantId={tid}
              tenantName={tenantName}
              onSave={() =>
                void saveConfig({
                  seo_meta_title: seoTitle || null,
                  seo_meta_description: seoDescription || null,
                  seo_og_image_url: seoOgImage || null,
                })
              }
            />
          ) : null}

          {activeTab === "suscripcion" ? (
            <SubscriptionSettingsPanel
              planId={planId}
              planLimits={planLimits}
              trialEndsAt={tenant.trialEndsAt ?? null}
              lifecycleStatus={tenant.lifecycleStatus ?? null}
              validUntil={planValidUntil}
            />
          ) : null}

          {activeTab === "uso" ? (
            <SettingsPanelSuspense>
              <LazyUsageGaugesPanel />
            </SettingsPanelSuspense>
          ) : null}

          {activeTab === "api" ? (
            <SettingsPanelSuspense>
              <LazyApiKeysPanel />
            </SettingsPanelSuspense>
          ) : null}

          {activeTab === "soporte" ? (
            <TenantSupportPanel />
          ) : null}

          {activeTab === "equipo" ? (
            tid ? (
              <SettingsPanelSuspense>
                <LazyTeamSettingsPanel tenantId={tid} />
              </SettingsPanelSuspense>
            ) : null
          ) : null}

          {activeTab === "publicar" ? (
            <div className="w-full max-w-[1400px] space-y-4">
              {tid && tenant ? (
                <CustomDomainField
                  tenantId={tid}
                  value={tenant.customDomain}
                  status={tenant.customDomainStatus}
                  checkError={tenant.customDomainCheckError}
                  verifiedAt={tenant.customDomainVerifiedAt}
                  onSaved={(domain, patch) => {
                    setTenant((prev) =>
                      prev
                        ? {
                            ...prev,
                            customDomain: domain,
                            customDomainStatus: patch?.status ?? prev.customDomainStatus,
                            customDomainCheckError: patch?.checkError ?? prev.customDomainCheckError,
                          }
                        : prev,
                    );
                  }}
                />
              ) : null}
              <PublishSettingsPanel
                tenantName={tenantName}
                slug={tenant?.slug ?? ""}
                primaryColor={config?.primaryColor ?? "#16a34a"}
                backgroundColor={backgroundColor}
                widgetConfig={widgetConfig}
                onWidgetConfigChange={setWidgetConfig}
                onSaveWidget={() => void saveWidgetConfig()}
                saving={busy}
                hasStorefront={hasStorefront}
              />
            </div>
          ) : null}
          </>
        ) : null}
      </ClinicalPageShell>
    </div>
  );
}
